<script setup lang="ts">
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { Card } from '@taler/ui'
import type { Locale } from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { plainAmount, signTone, useAccountingView } from '@/app/accounting-view'
import type { FlowTotals } from '../internal/types.ts'

const props = defineProps<{
  totals: FlowTotals | undefined
  accountClass: string | undefined
  locale: Locale
  isLoading: boolean
}>()

const { t } = useI18n()
const { accountingView } = useAccountingView()

const cards = computed(() => {
  const totals = props.totals
  if (totals === undefined) return []
  const entries = { key: 'entries', label: t('accountDetail.cards.entries'), count: totals.count }
  const net = plainAmount(totals.net, props.accountClass, accountingView.value)
  const transfers = {
    key: 'transfers',
    label: t('accountDetail.cards.transfers'),
    transfers: [
      { label: t('accountDetail.cards.transfersIn'), money: totals.transfersIn },
      { label: t('accountDetail.cards.transfersOut'), money: totals.transfersOut },
    ],
  }
  const adjustments = {
    key: 'adjustments',
    label: t('accountDetail.cards.adjustments'),
    money: totals.adjustments,
    sign: true,
    tone: undefined,
  }
  if (props.accountClass === 'INCOME') {
    return [
      entries,
      {
        key: 'total',
        label: t('accountDetail.cards.income'),
        money: net,
        sign: true,
        tone: signTone(net),
      },
      transfers,
      adjustments,
    ]
  }
  if (props.accountClass === 'EXPENSE') {
    return [
      entries,
      {
        key: 'total',
        label: t('accountDetail.cards.spending'),
        money: net,
        sign: true,
        tone: signTone(net),
      },
      transfers,
      adjustments,
    ]
  }
  return [
    entries,
    {
      key: 'in',
      label: t('accountDetail.cards.moneyIn'),
      money: totals.inflow,
      sign: false,
      tone: 'income' as const,
    },
    {
      key: 'out',
      label: t('accountDetail.cards.moneyOut'),
      money: totals.outflow,
      sign: false,
      tone: 'expense' as const,
    },
    transfers,
    adjustments,
    { key: 'net', label: t('accountDetail.cards.net'), money: net, sign: true, tone: undefined },
  ]
})
</script>

<template>
  <dl class="grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-[var(--space-group)]">
    <Card
      v-for="card in cards"
      :key="card.key"
      padding="block"
      class="flex min-w-0 flex-col gap-[var(--space-inline)]"
    >
      <dt
        class="text-[length:var(--text-step-1)] font-semibold uppercase tracking-[var(--tracking-caps)] text-[color:var(--color-text-muted)]"
      >
        {{ card.label }}
      </dt>
      <dd
        v-if="'count' in card"
        class="text-[length:var(--text-step-5)] font-semibold tabular-nums text-[color:var(--color-text)]"
      >
        {{ card.count }}
      </dd>
      <dd v-else-if="'transfers' in card" class="flex flex-col gap-[var(--space-inline)]">
        <span
          v-for="leg in card.transfers"
          :key="leg.label"
          class="flex items-baseline justify-between gap-[var(--space-inline)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)]"
        >
          {{ leg.label }}
          <MoneyAmount :value="leg.money" tone="transfer" />
        </span>
      </dd>
      <dd v-else class="text-[length:var(--text-step-4)] font-semibold tabular-nums">
        <MoneyAmount :value="card.money" :sign="card.sign" :tone="card.tone" size="medium" />
      </dd>
    </Card>
  </dl>
  <p v-if="isLoading" class="sr-only" role="status">{{ t('accountDetail.loading') }}</p>
</template>
